/*
Should $orders be named as such, or something else?
There should be a more efficient way of grabbing products and orders than `.find()`
*/

import { browser } from "$app/environment";
import { m } from "$lib/paraglide/messages";
import { getAPIProductsPromise } from "$lib/subscription-service";
import { showAlert } from "@brave/leo/src/components/alert/alertCenter.svelte";
import { checkBrowserSupport, hasBrowserSkusService, isBrave, isLinux } from "./utils";

const FETCH_PRODUCTS_MAX_ATTEMPTS = 3;

async function fetchProductData(): Promise<CoreProduct[]> {
  for (let attempt = 1; attempt <= FETCH_PRODUCTS_MAX_ATTEMPTS; attempt++) {
    try {
      const apiProducts = await getAPIProductsPromise();
      if (!Array.isArray(apiProducts)) {
        throw new Error("Unexpected /products response");
      }

      // Transform the response into the expected CoreProduct structure
      const productMap: Map<string, CoreProduct> = new Map();
      for (let apiProduct of apiProducts) {
        const price: Price = {
          id: apiProduct.product_id,
          period: apiProduct.period,
          currency: apiProduct.currency,
          price: parseFloat(apiProduct.price),
        };

        let product: CoreProduct | undefined;
        if ((product = productMap.get(apiProduct.name))) {
          product.prices.push(price);
        } else {
          product = {
            name: apiProduct.name,
            prices: [price],
          };
          if (apiProduct.trial_days) {
            product.trialDays = apiProduct.trial_days;
          }
        }
        productMap.set(apiProduct.name, product);
      }

      return Array.from(productMap.values());
    } catch (error) {
      console.error(`Failed to fetch products (attempt ${attempt}/${FETCH_PRODUCTS_MAX_ATTEMPTS})`, error);
      if (attempt === FETCH_PRODUCTS_MAX_ATTEMPTS && browser) {
        const message = error instanceof Error ? error.message : String(error);
        showAlert({ content: message, type: "error", actions: [] }, 0, true);
      }
    }
  }

  // If all attempts fail, return an empty list so the UI can degrade gracefully.
  return [];
}

export type ProductName = CoreProduct['name'];

// Define Currency types
export const currencies = ["USD"] as const;
export type Currency = (typeof currencies)[number];

// Define Period (or term) type and period options
export const periods = ["month", "year", "one-off"] as const;
export type Period = (typeof periods)[number];
type PeriodOption = {
  name: string;
  description: string;
  suffix: string;
  promoSuffix?: Function;
};
export const periodOptions = new Map<Period, PeriodOption>()
  .set("month", {
    name: m.monthly_period(),
    description: m.monthly_payment_description(),
    suffix: m.monthly_suffix(),
  })
  .set("year", {
    name: m.annual_period(),
    description: m.annual_payment_description(),
    suffix: m.annual_suffix(),
    promoSuffix: m.annual_promo_suffix,
  })
  .set("one-off", {
    name: m.one_off_period(),
    description: m.one_off_payment_description(),
    suffix: m.one_off_suffix(),
  });

// Define Product type and subtypes
export type Price = {
  id: string;
  period: Period;
  currency: Currency;
  price: number;
};

export type CoreProduct = {
  name: string;
  prices: Price[];
  trialDays?: number;
};

type ExtraProductProps = {
  baseName: string;
  baseNameEn: string;
  displayName: string;
  description: string;
  link: string;
  features: Array<string>;
  hasFreeVersion: boolean;
  freeFeatures?: Array<string>;
  actionPhrase: string;
  usesNativeSDK: boolean;
  weight?: number;
  browserSupport?: { isSupported: boolean; message?: string };
  isUnlisted?: boolean;
  disableImplicitFlows?: boolean;
  localStorageKeyPrefix?: string;
};

// This type combines CoreProduct and ExtraProductProps, but changes the types for "prices" and "name"
export type Product = Omit<CoreProduct & ExtraProductProps, "prices" | "name"> & {
  prices: Map<string, Price>;
  name: ProductName;
};
export type Products = Array<Product>;

// Define site specific product data
export const extraProductInfo: Record<string, ExtraProductProps> = {
  talk: {
    baseName: m.talk_base_name(),
    baseNameEn: m.talk_base_name({}, { locale: "en" }),
    displayName: m.talk_display_name(),
    description: m.talk_description(),
    link: "https://brave.com/talk/",
    features: [m.talk_feature_1(), m.talk_feature_2(), m.talk_feature_3()],
    hasFreeVersion: true,
    freeFeatures: [m.talk_free_feature()],
    actionPhrase: m.talk_action_phrase(),
    usesNativeSDK: false,
    browserSupport: checkBrowserSupport(),
  },
  search: {
    baseName: m.search_base_name(),
    baseNameEn: m.search_base_name({}, { locale: "en" }),
    displayName: m.search_display_name(),
    description: m.search_description(),
    link: "#",
    features: [m.search_feature_1(), m.search_feature_2(), m.search_feature_3()],
    hasFreeVersion: true,
    actionPhrase: m.search_action_phrase(),
    usesNativeSDK: false,
    browserSupport: checkBrowserSupport(() => {
      if (!isBrave()) {
        return m.search_exclusive_message();
      }
    }),
  },
  vpn: {
    baseName: m.vpn_base_name(),
    baseNameEn: m.vpn_base_name({}, { locale: "en" }),
    displayName: m.vpn_display_name(),
    description: m.vpn_description(),
    link: "#",
    features: [
      m.vpn_feature_1(),
      m.vpn_feature_2(),
      m.vpn_feature_3(),
      m.vpn_feature_4(),
      m.vpn_feature_5(),
      m.vpn_feature_6(),
    ],
    hasFreeVersion: false,
    actionPhrase: m.vpn_action_phrase(),
    weight: 2,
    usesNativeSDK: true,
    localStorageKeyPrefix: "braveVpn",
    browserSupport: checkBrowserSupport(() => {
      if (!isBrave()) {
        return m.vpn_exclusive_message();
      }

      if (isLinux()) {
        return m.vpn_linux_unsupported_message();
      }

      if (!hasBrowserSkusService()) {
        return m.vpn_version_incompatible_message();
      }
    }),
  },
  leo: {
    baseName: m.leo_base_name(),
    baseNameEn: m.leo_base_name({}, { locale: "en" }),
    displayName: m.leo_display_name(),
    description: m.leo_description(),
    link: "#",
    features: [m.leo_feature_1(), m.leo_feature_2(), m.leo_feature_3()],
    hasFreeVersion: true,
    actionPhrase: m.leo_action_phrase(),
    weight: 1,
    usesNativeSDK: true,
    localStorageKeyPrefix: "braveLeo",
    browserSupport: checkBrowserSupport(() => {
      if (!isBrave()) {
        return m.leo_exclusive_message();
      }

      if (!hasBrowserSkusService()) {
        return m.leo_version_incompatible_message();
      }
    }),
  },
  origin: {
    baseName: m.origin_base_name(),
    baseNameEn: m.origin_base_name({}, { locale: "en" }),
    displayName: m.origin_display_name(),
    description: m.origin_description(),
    link: "#",
    features: [m.origin_feature_1(), m.origin_feature_2(), m.origin_feature_3(), m.origin_feature_4()],
    hasFreeVersion: false,
    actionPhrase: m.origin_action_phrase(),
    usesNativeSDK: true,
    localStorageKeyPrefix: "braveOrigin",
    browserSupport: checkBrowserSupport(),
  },
};

// Utility function for easily building key to obtain price based on user selections
export const getPriceKey = (currency: Currency, period: Period) => {
  return `${currency?.toLowerCase()}${period?.toLowerCase()}`;
};

let products: Product[] = [];
// Merge core product data with site specific product data
export async function getProducts () {
  if (products && products.length > 0) return products;
  const productData = await fetchProductData();
  return products = productData
    .filter((p) => !!extraProductInfo[p.name])
    .map((p) => {
      let product: Product = {} as Product;

      // Ensure prop names from server are mapped to those used by frontend
      Object.assign(product, p);

      const prices: Map<string, Price> = new Map();
      for (let price of p.prices as unknown as Price[]) {
        const priceKey = getPriceKey(price.currency, price.period);
        prices.set(priceKey, price);
      }

      // Merge core product props with extra info needed for display
      product = {
        ...product,
        prices,
        ...extraProductInfo[p.name],
      };

      return product;
    });
}

export const getProductByPriceId = (priceId: string): Product | undefined => {
  return products?.find(({ prices }) => {
    return [...prices.values()].find(({ id }) => id === priceId);
  });
};

export const getProductByName = (name: string): Product => {
  return products?.find((product: Product) => {
    return name === product.name;
  }) as Product;
};
